import { createAsyncThunk } from '@reduxjs/toolkit';
import { BranchCode } from 'api/risk';
import { nextStep } from 'features/subscription-process/slices/thunks/nextStepThunk';
import { MriStepIdentifier, StepIdentifier, UnemploymentStepIdentifier } from 'features/subscription-process/types/enums';
import { toast } from 'react-toastify'; // ou votre système de toast
import { quotesAPI } from 'services/swagger-api';
import { parseApiError } from 'shared/utils/apiValidationErrors';
import { resetShoppingCart } from '../pricingSlice';

interface CreateDevisParams {
  mappedShoppingCartItems: any[]; // Remplacez par le bon type
  navigate: (path: string) => void;
  setIsOpen: (isOpen: boolean) => void;
  branchCode: BranchCode;
  riskId: string;
}

/** Les enums d'étapes sont numériques et diffèrent par branche : l'étape contractualisation doit suivre la branche. */
const contractualizationStepFor = (branchCode: BranchCode): StepIdentifier =>
  branchCode === BranchCode.Mri ? MriStepIdentifier.Contractualization : UnemploymentStepIdentifier.Contractualization;

export const createQuote = createAsyncThunk(
  'quotes/createDevis',
  async ({ mappedShoppingCartItems, navigate, setIsOpen, riskId, branchCode }: CreateDevisParams, { dispatch, rejectWithValue }) => {
    try {
      if (mappedShoppingCartItems) {
        const response = await quotesAPI.createDevisOptim({
          createQuoteCommandDto: { quotes: mappedShoppingCartItems }
        });

        if (response.isSuccess) {
          dispatch(resetShoppingCart());
        }

        response.errors?.forEach(message => toast.error(message));

        dispatch(nextStep({
          stepIdentifier: contractualizationStepFor(branchCode),
          isValid: false,
          isCurrent: true,
        }));

        // The back routes each quote to its file: a quote meant for the manager of a company goes
        // to the file "for the manager". The other files of the group are reached from the file
        // selector of the contractualisation page.
        const quotes = response.quotes ?? [];
        const target = quotes.find(quote => quote.riskId === riskId) ?? quotes[0];
        navigate(target
          ? `/simulation/contractualisation/${target.riskId}/${target.quoteId}`
          : `/simulation/contractualisation/${riskId}/${response.devisId}`);
        setIsOpen(false);

        return response;
      } else {
        throw new Error('No mapped shopping cart items');
      }
    } catch (error) {
      console.error("Error creating quote:", error);
      // Message du back (ex. 403 « aucune personne morale ») plutôt qu'un échec muet.
      const { message } = await parseApiError(error);
      toast.error(message || "Une erreur est survenue lors de la création du devis.");
      setIsOpen(false);
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);
